import { useCallback, useEffect, useState } from 'react';
import { Flex } from 'antd';
import {
  useAppSelector,
  useRequestTypeFinderById,
  useStatusFinderByNum,
} from '@Hooks/index';

import { ItRequisitionStatusCodes } from '@Enum/api/ItRequisitionStatusCodes';
import {
  useGetExistingPackageMutation,
  useGetITRequestFormQuery,
} from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import { Status } from '@Components/Base';
import {
  getITAccessories,
  getOrganizationOptions,
} from '@Store/selectors/dictionarySelector';
import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import { useGetProjectsForOrgMutation } from '@Store/api/dictionaryApiSlice';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import {
  useDownloadDocumentQuery,
  useFormListMutation,
} from '@Store/api/dmsApiSlice';
import { Option } from '@Components/types/options';

import {
  transformDictToOptions,
  transformProjectsToOptions,
} from '@Utils/transformToOptions';
import { getDocument } from '@Features/utils/document';

import { getDocumentFolder } from '../utils/document';
import fieldSource from './FieldSource';
import styles from './view-form.module.css';

const RequestViewForm: React.FC = () => {
  const { findStatus } = useStatusFinderByNum('ITRequisition');
  const { findRequestType } = useRequestTypeFinderById('ITRequisition');
  const [getExistingPackage] = useGetExistingPackageMutation();
  const [getProjects] = useGetProjectsForOrgMutation();
  const [projects, setProjects] = useState<Option[]>([]);
  const [documentId, setDocumentId] = useState('');

  const { data: imageURL } = useDownloadDocumentQuery(documentId || '', {
    skip: !documentId,
  });

  const [existingValue, setExistingValue] = useState('');

  const currentUser = useAppSelector(currentUserSelector);
  const requestId = useAppSelector(getModalRecordKey);
  const organizations = useAppSelector(getOrganizationOptions);
  const itAccessories = useAppSelector(getITAccessories);

  const { data } = useGetITRequestFormQuery(requestId!, {
    skip: !requestId,
    refetchOnMountOrArgChange: true,
  });

  const { data: users = [] } = useGetUsersListByNameQuery(null);
  const [getFormList] = useFormListMutation();

  useEffect(() => {
    if (!data?.fileName) {
      return;
    }

    const documentFolderPath = getDocumentFolder(data.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, data.fileName);

        if (matchedDocument) {
          setDocumentId(matchedDocument.documentId);
        }
      });
  }, [data]);

  useEffect(() => {
    if (data) {
      if (data.statusId === ItRequisitionStatusCodes.AWAITING_APPROVAL) {
        getExistingPackage(data.reqItemId)
          .unwrap()
          .then((response) => setExistingValue(response?.packages || ''));
      }
    }
  }, [data]);

  useEffect(() => {
    if (data && data.organizations) {
      getProjects(data.organizations.join(''))
        .unwrap()
        .then((projectsResp) => {
          const transformedProjects = transformProjectsToOptions(projectsResp);
          setProjects(transformedProjects);
        })
        .catch(() => {
          setProjects([]);
        });
    }
  }, [data]);

  const handleDownloadClick = useCallback((): void => {
    if (imageURL && data?.fileName) {
      const a = document.createElement('a');
      a.href = imageURL;
      a.download = data?.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }, [imageURL, data?.fileName]);

  if (!data) return null;

  return (
    <Flex className={styles.viewForm} vertical>
      {fieldSource(
        data,
        findRequestType,
        organizations,
        projects,
        transformDictToOptions(itAccessories),
        users,
        currentUser,
        documentId,
        existingValue,
        handleDownloadClick
      ).map((item) => (
        <div className={styles.viewItem} key={item.label}>
          <span>{item.label}</span>
          {item.label === 'Status' ? (
            <Status
              className={styles.viewItemValue}
              text={findStatus(Number(item.value))?.codeDesc || ''}
            />
          ) : (
            <span className={styles.viewItemValue}>{item.value}</span>
          )}
        </div>
      ))}
    </Flex>
  );
};

export default RequestViewForm;
